import { UpdateProfileStatus, type UpdateSuitabilityInput, useCustomerDetails, useUpdateSuitability } from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { type FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { SuitabilityField } from '../../types/suitability/suitability-types'

const log = new Logger('Brokerage Affiliation')

interface SuitabilityFormInterface {
  [SuitabilityField.IS_BROKER_DEALER]: 0 | 1
}

export const EditBrokerageAffiliationField: FC = () => {
  const { t } = useTranslation('features-settings')

  const { isOpen, openModal, closeModal } = useModalState()

  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isFetching } = useCustomerDetails()

  const show = data?.customer.isBrokerDealer !== undefined

  const value = data?.customer.isBrokerDealer ? 1 : (0 as 0 | 1) // Changing to number to acquiesce to Radio Group Values (boolean is problematic)

  const defaultValues: SuitabilityFormInterface = {
    [SuitabilityField.IS_BROKER_DEALER]: value,
  }

  log.debug({ defaultValues })

  const { handleSubmit, control, watch, reset, formState } = useForm<SuitabilityFormInterface>({
    mode: 'all',
    defaultValues,
  })

  const { mutate: updateSuitability, isLoading } = useUpdateSuitability({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating brokerage affiliation', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const clientIsAffiliated = watch(SuitabilityField.IS_BROKER_DEALER)

  const onSubmit = (formData: SuitabilityFormInterface): void => {
    setUpdateError(false)
    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug('Submitting Form', { formData })
    // 0 | 1 being used as values because of radio option values are booleans hence conversions
    void updateSuitability(formData as unknown as UpdateSuitabilityInput)
  }

  return show ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        // TODO - Uncomment when onEdit is implemented IN BE
        onEdit={openModal}
        value={t('employment_labels.brokerage_affiliation')}
        rightValue={t(`broker_affiliation.${String(value)}`)}
      />
      <ModalDrawer
        title={t('brokerage_affiliation.title')}
        isOpen={isOpen}
        onClose={() => {
          closeModal()
          setUpdateError(false)
          reset()
        }}
      >
        <form
          id="updateSuitabilityForm"
          className="flex flex-col gap-4 h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col">
            <div>
              <p className="typo-primary-base-regular text-text-body">{t('brokerage_affiliation.description')}</p>
              <Controller
                name={SuitabilityField.IS_BROKER_DEALER}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    className="my-xs gap-sm"
                    options={[
                      {
                        value: 0,
                        label: 'No',
                      },
                      {
                        value: 1,
                        label: 'Yes',
                      },
                    ]}
                  />
                )}
              />
            </div>
            {clientIsAffiliated ? (
              <p className="typo-primary-sm-light text-text-body">{t('brokerage_affiliation.if_yes')}</p>
            ) : null}
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateBrokerageAffiliation"
            type="submit"
            isLoading={isLoading || isFetching}
            isDisabled={!formState.isValid}
            className="mt-4 md:mt-0"
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
